/**
 * Print PublishElement data grouped by Element type
 * @param data {Object[]} Array of Element data objects
 */
export function printElementTypes(data) {

    // console.log('printElementTypes', data)
    const elementsByType = {}


    data?.forEach(elementData => {
        if (!elementData.type) {
            throw new Error('Element type not defined')
        }

        if (!elementsByType[elementData.type])
            elementsByType[elementData.type] = []

        elementsByType[elementData.type].push(elementData)
    })
    // console.log('elementsByType', elementsByType)


    const groupedByProperties = {}
    Object.entries(elementsByType).forEach(([elementType, elementDataArray]) => {
        groupedByProperties[elementType] = elementDataArray.reduce((map, elementData) => {
            Object.entries(elementData).forEach(([elementProperty, propertyValue]) => {
                if (!map[elementProperty]) map[elementProperty] = []
                map[elementProperty].push(propertyValue)
            })
            return map
        }, {})
    })
    // console.log('groupedByProperties', groupedByProperties)


    const commonProperties = new Set()
    Object.values(groupedByProperties).forEach(groupedProps => {
        Object.keys(groupedProps).forEach(propKey => {
            if (Object.values(groupedByProperties).every(elementPropGroup => elementPropGroup[propKey] !== undefined)) {
                commonProperties.add(propKey)
            }
        })
    })
    // console.log('commonProperties', commonProperties)


    const commonPropertiesSorted = Array.from(commonProperties).sort()
    // console.log('commonPropertiesSorted', commonPropertiesSorted)


    const groupedByPropertiesWithoutCommon = {}
    Object.entries(groupedByProperties).forEach(([elementType, groupedProps]) => {
        groupedByPropertiesWithoutCommon[elementType] = { ...groupedProps }
        commonPropertiesSorted.forEach(commonProperty => {
            delete groupedByPropertiesWithoutCommon[elementType][commonProperty]
        })
    })
    // console.log('groupedByPropertiesWithoutCommon', groupedByPropertiesWithoutCommon)


    const groupedByPropertiesWithoutCommonSplitByOptional = {}
    Object.entries(groupedByPropertiesWithoutCommon).forEach(([elementType, groupedProps]) => {
        groupedByPropertiesWithoutCommonSplitByOptional[elementType] = {
            optional: {},
            always: {}
        }

        const maxPropsNum = Object.values(groupedProps).reduce((maxLength, propValueArray) => Math.max(propValueArray.length, maxLength), 0)

        Object.entries(groupedProps).forEach(([propKey, propValueArray]) => {
            groupedByPropertiesWithoutCommonSplitByOptional[elementType][propValueArray.length < maxPropsNum ? 'optional' : 'always'][propKey] = propValueArray
        })

        for (const key of ['optional', 'always'])
            if (!Object.keys(groupedByPropertiesWithoutCommonSplitByOptional[elementType][key]).length)
                delete groupedByPropertiesWithoutCommonSplitByOptional[elementType][key]
    })
    // console.log('groupedByPropertiesWithoutCommonSplitByOptional', groupedByPropertiesWithoutCommonSplitByOptional)


    const groupedByPropertiesWithoutCommonSplitBySameType = {}
    Object.entries(groupedByPropertiesWithoutCommon).forEach(([elementType, groupedProps]) => {
        groupedByPropertiesWithoutCommonSplitBySameType[elementType] = {
            same: {},
            different: {}
        }

        Object.entries(groupedProps).forEach(([propKey, propValueArray]) => {
            groupedByPropertiesWithoutCommonSplitBySameType[elementType][propValueArray.length == 1 || propValueArray.reduce((accumulator, propValue) => {
                return accumulator === typeof propValue ? accumulator : false
            }, typeof propValueArray[0]) ? 'same' : 'different'][propKey] = propValueArray
        })

        for (const key of ['same', 'different'])
            if (!Object.keys(groupedByPropertiesWithoutCommonSplitBySameType[elementType][key]).length)
                delete groupedByPropertiesWithoutCommonSplitBySameType[elementType][key]
    })
    // console.log('groupedByPropertiesWithoutCommonSplitBySameType', groupedByPropertiesWithoutCommonSplitBySameType)


    const groupedByPropertiesSplitBySameType = {}
    Object.entries(groupedByProperties).forEach(([elementType, groupedProps]) => {
        groupedByPropertiesSplitBySameType[elementType] = {
            same: {},
            different: {}
        }

        Object.entries(groupedProps).forEach(([propKey, propValueArray]) => {
            groupedByPropertiesSplitBySameType[elementType][propValueArray.length == 1 || propValueArray.reduce((accumulator, propValue) => {
                return accumulator === typeof propValue ? accumulator : false
            }, typeof propValueArray[0]) ? 'same' : 'different'][propKey] = propValueArray
        })

        for (const key of ['same', 'different'])
            if (!Object.keys(groupedByPropertiesSplitBySameType[elementType][key]).length)
                delete groupedByPropertiesSplitBySameType[elementType][key]
    })
    // console.log('groupedByPropertiesSplitBySameType', groupedByPropertiesSplitBySameType)


    const justProperties = {}
    Object.values(groupedByProperties).forEach(elementPropertiesMap => {
        Object.entries(elementPropertiesMap).forEach(([propertyKey, propertyValuesArray]) => {
            if (!justProperties[propertyKey]) justProperties[propertyKey] = []
            justProperties[propertyKey].push(...propertyValuesArray)
        })
    })
    // console.log('justProperties', justProperties)

    const propertyTypes = {}
    Object.entries(justProperties).forEach(([propertyKey, propertyValuesArray]) => {
        propertyTypes[propertyKey] = [...new Set(propertyValuesArray.map(val => {
            if (typeof val != 'object') return typeof val
            if (val === null) return null
            if (Array.isArray(val)) return 'array'

            return 'NOTCAUGHT'

        }))]
    })
    // console.log('propertyTypes', propertyTypes)

    const propertyTypesOnlyNull = []
    Object.entries(propertyTypes).forEach(([propertyKey, propertyTypesArray]) => {
        if (propertyTypesArray.length == 1 && propertyTypesArray[0] === null)
            propertyTypesOnlyNull.push(propertyKey)
    })
    // console.log('propertyTypesOnlyNull', propertyTypesOnlyNull)
}

/**
 * Returns an array of allowed file types (usable with <input> tags) for a given media type
 * @param type {string} One of 'picture' | 'video' | 'pdf' | 'ppt' | 'other'
 * @returns {string[]}
 */
export function getAllowedFileTypesForMediaType(type) {
    switch (type) {
        case 'picture':
            return Laravel.allowedImageFileTypes;
        case 'video':
            return Laravel.allowedVideoFileTypes;
        case 'pdf':
            return Laravel.allowedPDFFileTypes;
        case 'ppt':
            return Laravel.allowedPPTFileTypes;
        case 'other':
            return Laravel.allowedOtherFileTypes;
    }
}

/**
 * Returns a media type for a given allowed file type
 * @param allowedFileType {string} One of `Laravel.allowedFileTypes`
 * @returns {string}
 */
export function getMediaTypeForAllowedFileType(allowedFileType) {
    switch(true) {
        case Laravel.allowedImageFileTypes.includes(allowedFileType):
            return 'picture'
        case Laravel.allowedVideoFileTypes.includes(allowedFileType):
            return 'video'
        case Laravel.allowedPDFFileTypes.includes(allowedFileType):
            return 'pdf'
        case Laravel.allowedPPTFileTypes.includes(allowedFileType):
            return 'ppt'
        case Laravel.allowedOtherFileTypes.includes(allowedFileType):
            return 'other'
    }
}

/** @type {{[allowedFileType: string]: string}} */
export const allowedFileTypesToMediaTypeMap = Object.fromEntries(
    Laravel.allowedFileTypes.map(
        allowedFileType => ([allowedFileType, getMediaTypeForAllowedFileType(allowedFileType)])
    )
);

/** @type {{[mediaType: string]: string}} */
export const mediaTypeToTranslationMap =
    {
        picture: Lang.langGet('Picture'),
        video: Lang.langGet('Video'),
        pdf: Lang.langGet('PDF'),
        ppt: Lang.langChoice('PowerPoint|PowerPoints', 1),
        other: Lang.langChoice('Other|Other', 1)
    }

/** @type {{[mediaType: string]: number}} */
export const mediaTypeToSortOrderMap =
    {
        picture: 1,
        video: 2,
        pdf: 3,
        ppt: 4,
        other: 5
    }
